import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs/internal/Observable';
import { Notification } from '../models/notification';
import { NotificationSettings } from '../models/notification-settings';
import { PaginatedResponse } from './paginated-response.interface';
import { ConfigService } from '../services/config.service';

export interface NotificationFilters {
  protected?: boolean;
  not_viewed?: boolean;
  important?: boolean;
}

@Injectable({providedIn: 'root'})
export class NotificationBaseApi {
  protected apiUrl: string;

  constructor(
    private httpClient: HttpClient,
    private configService: ConfigService,
  ) {}

  async getCount(): Promise<number> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}${this.apiUrl}/notification/count/`)
      .pipe(map(data => +data))
      .toPromise();
  }

  async getList(page: number, size: number, filters: NotificationFilters = {}): Promise<PaginatedResponse<Notification[]>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    for (const key of Object.keys(filters)) {
      params = params.set(key, filters[key]);
    }

    return this.httpClient
      .get(`${this.configService.get('apiUrl')}${this.apiUrl}/notification/`, {params, observe: 'response'})
      .pipe(
        map(response => {
          const data: Notification[] = Notification.toFront(response.body);
          return {
            total: +response.headers.get('x-page-count'),
            data
          };
        }),
      ).toPromise();
  }

  async markViewed(notificationId: number, viewed: boolean): Promise<Notification> {
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}${this.apiUrl}/notification/${notificationId}/viewed/`, {viewed})
      .pipe(map(data => Notification.toFront(data)))
      .toPromise();
  }

  async markAllViewed(): Promise<Notification[]> {
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}${this.apiUrl}/notification/viewed/`, {})
      .pipe(map(data => Notification.toFront(data)))
      .toPromise();
  }

  async getNotificationSettings(): Promise<NotificationSettings> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}${this.apiUrl}/notification_settings/`)
      .pipe(map(data => NotificationSettings.toFront(data)))
      .toPromise();
  }

  async updateNotificationSettings(data: NotificationSettings): Promise<NotificationSettings> {
    return this.httpClient.post(
      `${this.configService.get('apiUrl')}${this.apiUrl}/notification_settings/`,
      NotificationSettings.toBack(data)
    )
      .pipe(map(result => NotificationSettings.toFront(result)))
      .toPromise();
  }
}
